import { ReactNode, useLayoutEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { applyDocumentLanguage, normalizeLanguage } from "./language";
import { translateTextValue } from "./translations";

const TRANSLATABLE_ATTRIBUTES = ["placeholder", "title", "aria-label", "alt"];

interface TranslatedNode extends Node {
  __cineparagonOriginalText?: string;
  __cineparagonTranslatedText?: string;
}

const shouldSkipNode = (node: Node) => {
  const element = node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement;
  if (!element) return true;
  return Boolean(element.closest("script, style, code, pre, textarea, [data-no-translate]"));
};

const translateTextNode = (node: TranslatedNode, language: string) => {
  if (!node.nodeValue || shouldSkipNode(node)) return;
  if (
    node.__cineparagonOriginalText == null ||
    (
      node.nodeValue !== node.__cineparagonTranslatedText &&
      node.nodeValue !== node.__cineparagonOriginalText
    )
  ) {
    node.__cineparagonOriginalText = node.nodeValue;
  }
  const original = node.__cineparagonOriginalText;
  const translated = language === "en" ? translateTextValue(original, language) : original;
  node.__cineparagonTranslatedText = translated;
  if (node.nodeValue !== translated) {
    node.nodeValue = translated;
  }
};

const translateElementAttributes = (element: Node, language: string) => {
  if (!(element instanceof Element)) return;
  TRANSLATABLE_ATTRIBUTES.forEach((attribute) => {
    if (!element.hasAttribute(attribute)) return;
    const originalAttribute = `data-cineparagon-original-${attribute}`;
    const translatedAttribute = `data-cineparagon-translated-${attribute}`;
    const current = element.getAttribute(attribute) || "";
    if (
      !element.hasAttribute(originalAttribute) ||
      (
        current !== element.getAttribute(translatedAttribute) &&
        current !== element.getAttribute(originalAttribute)
      )
    ) {
      element.setAttribute(originalAttribute, current);
    }
    const original = element.getAttribute(originalAttribute) || "";
    const translated = language === "en" ? translateTextValue(original, language) : original;
    if (element.getAttribute(translatedAttribute) !== translated) {
      element.setAttribute(translatedAttribute, translated);
    }
    if (element.getAttribute(attribute) !== translated) {
      element.setAttribute(attribute, translated);
    }
  });
};

const translateTree = (root: Node | null, language: string) => {
  if (!root || typeof NodeFilter === "undefined") return;
  const normalizedLanguage = normalizeLanguage(language);

  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root, normalizedLanguage);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE) translateElementAttributes(root, normalizedLanguage);

  const elementWalker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  while (elementWalker.nextNode()) {
    translateElementAttributes(elementWalker.currentNode, normalizedLanguage);
  }

  const textWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (textWalker.nextNode()) {
    translateTextNode(textWalker.currentNode, normalizedLanguage);
  }
};

function LanguageBoundary({ children }: { children: ReactNode }) {
  const language = normalizeLanguage(useSelector((state) => state.settings.language));
  const rootRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    applyDocumentLanguage(language);
    // Most dialogs, notifications and tooltips are rendered through a portal
    // directly into document.body. Observing only the wrapper left that UI in
    // the previous language after a switch.
    const translationRoot = typeof document !== "undefined"
      ? document.body
      : rootRef.current;
    translateTree(translationRoot, language);

    if (!translationRoot || typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData") {
          translateTextNode(mutation.target, language);
          return;
        }
        mutation.addedNodes.forEach((node) => translateTree(node, language));
        if (mutation.type === "attributes") {
          translateElementAttributes(mutation.target, language);
        }
      });
    });

    observer.observe(translationRoot, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRIBUTES
    });

    return () => observer.disconnect();
  }, [language]);

  return <div ref={rootRef} className="language-boundary">{children}</div>;
}

export default LanguageBoundary;
